import { oc } from "@orpc/contract";
import { z } from "zod";

const UUIDSchema = z.uuid();

/**
 * AI Suggestion Entity Type Schema
 */
export const AiSuggestionEntityTypeSchema = z.enum([
  "staged_customer_purchase_order",
  "inbound_email",
]);

/**
 * AI Suggestion Type Schema
 */
export const AiSuggestionTypeSchema = z.enum([
  "approve_order",
  "send_email_draft",
]);

/**
 * AI Suggestion Status Schema
 */
export const AiSuggestionStatusSchema = z.enum([
  "generating", // Agent is currently running
  "pending",
  "accepted",
  "rejected",
  "invalidated",
  "dismissed",
  "failed", // Agent failed to complete
]);

/**
 * AI Suggestion Invalidated Reason Schema
 */
export const AiSuggestionInvalidatedReasonSchema = z.enum([
  "new_email_received",
  "action_taken",
  "manual",
]);

/**
 * Email Draft Schema (for embedded drafts in suggestions)
 */
export const EmailDraftSummarySchema = z.object({
  id: UUIDSchema,
  to: z.string(),
  cc: z.string().nullable().optional(),
  subject: z.string(),
  content: z.string(),
  htmlContent: z.string().nullable().optional(),
  isAiGenerated: z.boolean(),
  status: z.string(),
});

/**
 * Suggested Action Type Schema
 */
export const SuggestedActionTypeSchema = z.enum([
  "remove_line_item",
  "update_line_item_price",
  "create_discount",
  "send_email_draft",
  "approve_order",
  "update_order_dates",
  "update_ship_to_location",
  "update_line_item_product",
]);

/**
 * Suggested Email Draft Schema (embedded in action)
 */
export const SuggestedEmailDraftSchema = z.object({
  to: z.string(),
  subject: z.string(),
  body: z.string(),
  tone: z.enum(["professional", "friendly", "urgent", "apologetic"]).optional(),
});

/**
 * Suggested Action Schema
 */
export const SuggestedActionSchema = z.object({
  type: SuggestedActionTypeSchema,
  description: z.string(),
  // Original index in the suggestedActions array (for tracking applied actions)
  index: z.number(),
  // Type-specific fields
  lineItemIdentifier: z.string().optional(),
  lineItemIndex: z.number().optional(),
  newPrice: z.number().optional(),
  emailDraft: SuggestedEmailDraftSchema.optional(),
  // For update_order_dates action
  deliveryDate: z.string().optional(),
  shipDate: z.string().optional(),
  // For update_ship_to_location action
  locationId: z.string().optional(),
  locationName: z.string().optional(),
  // For update_line_item_product action
  productVariantId: z.string().optional(),
  customerProductId: z.string().optional(),
  productName: z.string().optional(),
  productSku: z.string().optional(),
  // System-set field for tracking applied actions
  applied: z.boolean().optional(),
});

/**
 * AI Suggestion Schema
 */
export const AiSuggestionSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  entityType: AiSuggestionEntityTypeSchema,
  entityId: UUIDSchema,
  suggestionType: AiSuggestionTypeSchema,
  status: AiSuggestionStatusSchema,
  emailDraftId: UUIDSchema.nullable().optional(),
  summary: z.string().nullable().optional(),
  actionReason: z.string().nullable().optional(),
  invalidatedAt: z.coerce.date().nullable().optional(),
  invalidatedReason: AiSuggestionInvalidatedReasonSchema.nullable().optional(),
  // Dismissal tracking
  dismissedAt: z.coerce.date().nullable().optional(),
  dismissedByUserId: UUIDSchema.nullable().optional(),
  // Error tracking (for failed status)
  errorMessage: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  // Embedded email draft if available
  emailDraft: EmailDraftSummarySchema.nullable().optional(),
  // All suggested actions from the agent (extracted from agentResponse)
  suggestedActions: z.array(SuggestedActionSchema).optional(),
});

/**
 * Get AI Suggestion Input Schema
 */
export const GetAiSuggestionInputSchema = z.object({
  id: UUIDSchema,
});

/**
 * Get Current AI Suggestion By Entity Input Schema
 * Used to fetch the current suggestion for a specific entity (e.g., staged order)
 * without needing to know the suggestion ID upfront.
 */
export const GetCurrentAiSuggestionByEntityInputSchema = z.object({
  entityType: AiSuggestionEntityTypeSchema,
  entityId: UUIDSchema,
});

/**
 * Get Current AI Suggestion By Entity Output Schema
 * Returns the suggestion or null if none exists
 */
export const GetCurrentAiSuggestionByEntityOutputSchema = z.object({
  suggestion: AiSuggestionSchema.nullable(),
});

/**
 * Accept AI Suggestion Input Schema
 */
export const AcceptAiSuggestionInputSchema = z.object({
  id: UUIDSchema,
  /** If true and suggestion is APPROVE_ORDER type, deletes the staged order draft after accepting */
  deleteDraft: z.boolean().optional(),
});

/**
 * Accept AI Suggestion Output Schema
 */
export const AcceptAiSuggestionOutputSchema = z.object({
  suggestion: AiSuggestionSchema,
  emailDraftSent: z.boolean().optional(),
  orderApproved: z.boolean().optional(),
  /** True if the staged order draft was deleted */
  draftDeleted: z.boolean().optional(),
});

/**
 * Dismiss AI Suggestion Input Schema
 */
export const DismissAiSuggestionInputSchema = z.object({
  id: UUIDSchema,
});

/**
 * Generate AI Suggestion Input Schema
 */
export const GenerateAiSuggestionInputSchema = z.object({
  entityType: AiSuggestionEntityTypeSchema,
  entityId: UUIDSchema,
  /** If true, generates even if there's a dismissed suggestion */
  force: z.boolean().optional(),
});

/**
 * Generate AI Suggestion Output Schema
 */
export const GenerateAiSuggestionOutputSchema = z.object({
  suggestion: AiSuggestionSchema.nullable(),
  message: z.string().optional(),
});

/**
 * Execute Suggested Action Input Schema
 */
export const ExecuteSuggestedActionInputSchema = z.object({
  suggestionId: UUIDSchema,
  actionIndex: z.number(),
  stagedOrderId: UUIDSchema,
  actionType: SuggestedActionTypeSchema,
  lineItemIdentifier: z.string().optional(),
  lineItemIndex: z.number().optional(),
  newPrice: z.number().optional(),
  // For update_order_dates action
  deliveryDate: z.string().optional(),
  shipDate: z.string().optional(),
  // For update_ship_to_location action
  locationId: z.string().optional(),
  // For update_line_item_product action
  productVariantId: z.string().optional(),
  customerProductId: z.string().optional(),
});

/**
 * Execute Suggested Action Output Schema
 */
export const ExecuteSuggestedActionOutputSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
  updatedOrder: z.record(z.string(), z.unknown()).optional(),
  // Include input params in response for audit logging (oRPC doesn't populate req.body)
  stagedOrderId: z.string().optional(),
  actionType: SuggestedActionTypeSchema.optional(),
});

/**
 * AI Suggestion Contracts
 */
export const aiSuggestionContracts = {
  // Get an AI suggestion by ID
  get: oc.input(GetAiSuggestionInputSchema).output(AiSuggestionSchema).route({
    method: "GET",
    path: "/ai-suggestions/{id}",
  }),

  // Get the current AI suggestion for an entity (by entity type and ID)
  // This is useful for polling without knowing the suggestion ID upfront
  getByEntity: oc
    .input(GetCurrentAiSuggestionByEntityInputSchema)
    .output(GetCurrentAiSuggestionByEntityOutputSchema)
    .route({
      method: "GET",
      path: "/ai-suggestions/by-entity/{entityType}/{entityId}",
    }),

  // Accept an AI suggestion (performs the suggested action)
  accept: oc
    .input(AcceptAiSuggestionInputSchema)
    .output(AcceptAiSuggestionOutputSchema)
    .route({
      method: "POST",
      path: "/ai-suggestions/{id}/accept",
    }),

  // Dismiss an AI suggestion
  dismiss: oc
    .input(DismissAiSuggestionInputSchema)
    .output(AiSuggestionSchema)
    .route({
      method: "POST",
      path: "/ai-suggestions/{id}/dismiss",
    }),

  // Generate a new AI suggestion for an entity
  generate: oc
    .input(GenerateAiSuggestionInputSchema)
    .output(GenerateAiSuggestionOutputSchema)
    .route({
      method: "POST",
      path: "/ai-suggestions/generate",
    }),

  // Execute a specific suggested action
  executeAction: oc
    .input(ExecuteSuggestedActionInputSchema)
    .output(ExecuteSuggestedActionOutputSchema)
    .route({
      method: "POST",
      path: "/ai-suggestions/execute-action",
    }),
} as const;

// Export types
export type AiSuggestionEntityType = z.infer<
  typeof AiSuggestionEntityTypeSchema
>;
export type AiSuggestionType = z.infer<typeof AiSuggestionTypeSchema>;
export type AiSuggestionStatus = z.infer<typeof AiSuggestionStatusSchema>;
export type AiSuggestionInvalidatedReason = z.infer<
  typeof AiSuggestionInvalidatedReasonSchema
>;
export type SuggestedActionType = z.infer<typeof SuggestedActionTypeSchema>;
export type SuggestedEmailDraft = z.infer<typeof SuggestedEmailDraftSchema>;
export type SuggestedAction = z.infer<typeof SuggestedActionSchema>;
export type AiSuggestion = z.infer<typeof AiSuggestionSchema>;
export type GetAiSuggestionInput = z.infer<typeof GetAiSuggestionInputSchema>;
export type GetCurrentAiSuggestionByEntityInput = z.infer<
  typeof GetCurrentAiSuggestionByEntityInputSchema
>;
export type GetCurrentAiSuggestionByEntityOutput = z.infer<
  typeof GetCurrentAiSuggestionByEntityOutputSchema
>;
export type AcceptAiSuggestionInput = z.infer<
  typeof AcceptAiSuggestionInputSchema
>;
export type AcceptAiSuggestionOutput = z.infer<
  typeof AcceptAiSuggestionOutputSchema
>;
export type DismissAiSuggestionInput = z.infer<
  typeof DismissAiSuggestionInputSchema
>;
export type GenerateAiSuggestionInput = z.infer<
  typeof GenerateAiSuggestionInputSchema
>;
export type GenerateAiSuggestionOutput = z.infer<
  typeof GenerateAiSuggestionOutputSchema
>;
export type ExecuteSuggestedActionInput = z.infer<
  typeof ExecuteSuggestedActionInputSchema
>;
export type ExecuteSuggestedActionOutput = z.infer<
  typeof ExecuteSuggestedActionOutputSchema
>;
